import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";

/**
 * OTP verification screen shown after registration OR when login rejects an
 * unverified account. On success it establishes a session and calls onVerified().
 */
export default function EmailOtpVerify({ email, onVerified, onChangeEmail }) {
  const { verifyEmail, resendVerification } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the 6-digit code from your email"); return; }
    setBusy(true);
    try {
      const user = await verifyEmail(email, code.trim());
      toast.success("Email verified — welcome!");
      onVerified?.(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await resendVerification(email);
      toast.success("New code sent — check your email");
      setCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to resend code";
      toast.error(detail);
      const m = /(\d+)\s*s/.exec(String(detail));
      if (m) setCooldown(parseInt(m[1], 10));
    }
  };

  return (
    <div className="w-full max-w-sm" data-testid="otp-verify-panel">
      <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Verify your email</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Enter the 6-digit code</h1>
      <p className="text-sm text-zinc-500 mb-2">
        We sent a code to <span className="font-medium text-zinc-900" data-testid="otp-email">{email}</span>. It expires in 10 minutes.
      </p>
      {onChangeEmail && (
        <button
          type="button"
          onClick={onChangeEmail}
          className="text-xs text-blue-600 hover:underline mb-6"
          data-testid="otp-change-email"
        >
          Use a different email
        </button>
      )}

      <form onSubmit={submit} className="space-y-4 mt-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">Verification code</label>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            placeholder="123456"
            className="w-full border border-zinc-300 px-3 py-3 text-lg tracking-[0.4em] font-mono text-center focus:border-zinc-900 focus:outline-none"
            data-testid="otp-code-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy || code.length < 4}
          className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="otp-verify-btn"
        >
          {busy ? "Verifying…" : <>Verify &amp; continue <ArrowRight size={14} weight="bold" /></>}
        </button>
      </form>

      <div className="mt-6 text-xs text-zinc-500 flex items-center gap-2">
        <EnvelopeSimple size={14} />
        <span>Didn&apos;t receive a code?</span>
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0}
          className="link-underline font-medium text-zinc-900 disabled:text-zinc-400 disabled:no-underline"
          data-testid="otp-resend-btn"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
        </button>
      </div>
    </div>
  );
}
